"use client"

import { Stack } from "@mantine/core"
import { IconExternalLink } from "@tabler/icons-react"
import countries from "i18n-iso-countries"
import enCountries from "i18n-iso-countries/langs/en.json"
import frCountries from "i18n-iso-countries/langs/fr.json"
import { useEffect, useState } from "react"

import {
  AgecActivityCountries,
  AgecComponentDetails,
  AgecMaterialDetails,
  AGECResponses,
} from "@tfg/types/endpoints/agec"

import { Select } from "../../../molecules/Inputs/Select/Select"
import { FooterAGEC } from "../Footer"
import { ActivityList } from "../page-components/ActivityList"

import styles from "./Jonak.module.css"

type Component = { displayName: string } & AgecComponentDetails

function defineComponentTranslation(
  lang: string,
  component: AgecComponentDetails
) {
  if (!component) {
    return ""
  }

  if (
    (component.translations.FR === "" && lang === "fr") ||
    (component.translations.EN === "" && lang === "en")
  ) {
    return component.translations.originalText
  }

  if (lang === "fr") {
    return component.translations.FR
  }
  return component.translations.EN
}

function defineMaterialTranslation(
  lang: string,
  material: AgecMaterialDetails
) {
  if (!material) {
    return ""
  }

  if (
    (material.translations.FR === "" && lang === "fr") ||
    (material.translations.EN === "" && lang === "en")
  ) {
    return material.translations.originalText
  }

  if (lang === "fr") {
    return material.translations.FR
  }
  return material.translations.EN
}

const shownCertificateNames = ["Leather Working Group", "Audit Footwear"]
function filterCertificates(certificate: { name: string }) {
  return shownCertificateNames.includes(certificate.name)
}

export const JonakAGECPage = ({
  agecData,
  t,
  currentLanguage,
}: {
  agecData: AGECResponses.GenericAgecData
  t: (key: string, options?: Record<string, string>) => string
  currentLanguage: string
}) => {
  const [components, setComponents] =
    useState<(Component & { materialItemId: number })[]>()
  const [selectedComponent, setSelectedComponent] = useState<
    (Component & { materialItemId: number }) | undefined
  >(components ? components[0] : undefined)

  countries.registerLocale(enCountries)
  countries.registerLocale(frCountries)

  useEffect(() => {
    const componentsTranslated = agecData.components.flatMap((component) => {
      const componentTranslation = defineComponentTranslation(
        currentLanguage,
        component
      )
      const materials = component.materials.map((m) => {
        const materialTranslation = defineMaterialTranslation(
          currentLanguage,
          m
        )
        return {
          materialItemId: m.itemId,
          displayName: `${componentTranslation} : ${m.proportion}% ${materialTranslation}`,
          ...component,
        }
      })

      return [...materials] satisfies (Component & { materialItemId: number })[]
    })
    setComponents(componentsTranslated)
    setSelectedComponent(componentsTranslated[0])
  }, [agecData.components, currentLanguage])

  if (!components || !selectedComponent) {
    return null
  }

  const agecActivityCountriesByComponentSelected = agecData
    .agecActivityCountriesByComponentsAndMaterials[selectedComponent?.itemId]?.[
    selectedComponent.materialItemId
  ] as AgecActivityCountries

  if (!agecActivityCountriesByComponentSelected) {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={styles.page}>
        <div className={styles.title}>
          {agecData.productAttributes.name}
          {agecData.productAttributes.color && " / "}
          {agecData.productAttributes.color}
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            {t("jonak:composition.header")}
          </div>
          {components &&
            components.map((comp, i) => {
              return (
                <div key={`comp-${i}`} className={styles.component}>
                  {comp.displayName}
                </div>
              )
            })}
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            {t("jonak:traceability.header")}
          </div>
          <p className={styles.paragraph}>
            {t("jonak:traceability.paragraph1")}
          </p>
          <p className={styles.paragraph}>
            {t("jonak:traceability.paragraph2")}
          </p>
        </div>
        <div className={styles.section}>
          <Select
            data={components.map((c) => ({
              value: c.materialItemId.toString(),
              label: c.displayName,
            }))}
            value={selectedComponent?.materialItemId.toString()}
            styles={{
              input: {
                backgroundColor: "#ede5de",
                border: "1px solid #000000",
              },
            }}
            onChange={(value) => {
              setSelectedComponent(
                components.find((c) => c.materialItemId.toString() === value)
              )
            }}
          />
          <ActivityList
            agecData={{
              ...agecData,
              agecActivityCountries: agecActivityCountriesByComponentSelected,
            }}
            showCertificates={true}
            certificateFilterFunction={filterCertificates}
            activitiesDepth={Infinity}
            showActivityIcons={false}
            showCountryFlags={true}
          />
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            {t("jonak:recyclability.header")}
          </div>
          <p className={styles.paragraph}>
            {t("jonak:recyclability.paragraph1.part1")}
            <a
              href={t("jonak:recyclability.paragraph1.href") ?? undefined}
              target="_blank"
              style={{ textDecoration: "underline" }}
              rel="noreferrer"
            >
              {t("jonak:recyclability.paragraph1.maintenance")}
              <IconExternalLink size={12} />
            </a>
            {t("jonak:recyclability.paragraph1.part2")}
          </p>
          <p className={styles.paragraph}>
            {t("jonak:recyclability.paragraph2")}
          </p>
          <p className={styles.paragraph}>
            {t("jonak:recyclability.paragraph3")}
          </p>
        </div>
        <div className={styles.section}>
          <a
            href=" https://www.jonak.fr/conseil_entretien.html#emballage"
            target="_blank"
          >
            <div
              className={styles.paragraph}
              style={{ textDecoration: "underline" }}
            >
              {t("jonak:packaging")}
              <IconExternalLink />
            </div>
          </a>
        </div>
        <Stack className={styles.footer} gap={2}>
          <FooterAGEC
            agecData={agecData}
            showLastUpdateTimestamp={true}
            showPoweredByTFG={true}
          />
        </Stack>
      </div>
    </div>
  )
}
